<script setup lang="ts">
import { ref, computed } from "vue";
import {
	ConditionRule,
	isDraftCondition,
} from "@/modules/rule_engine/ruleEngineClasses";
import ConfigureConditions from "@/modules/rule_engine/components/conditions/ConfigureConditions.vue";
import ConfigureActions from "@/modules/rule_engine/components/ConfigureActions.vue";
import { toRawDeep } from "@/Utils/to-raw-deep";

const emit = defineEmits(["store"]);

const props = defineProps<{
	rule: ConditionRule;
}>();

const isDialogVisible = defineModel<boolean>();

// Edit against a detached copy so cancelling leaves the original untouched.
const conditionRule = ref<ConditionRule>(toRawDeep(props.rule));

/**
 * Validity is shown inline and gates the save button, rather than being
 * reported by an alert dialog after the user has already tried to save.
 */
const problems = computed(() => {
	const found: string[] = [];

	if (conditionRule.value.conditions.length === 0) {
		found.push("Add at least one condition.");
	}
	if (conditionRule.value.conditions.some(isDraftCondition)) {
		found.push("Finish the unconfigured condition.");
	}
	if (conditionRule.value.actions.length === 0) {
		found.push("Add at least one action.");
	}

	return found;
});

const canSave = computed(() => problems.value.length === 0);

const storeConditionRule = () => {
	if (!canSave.value) return;

	emit("store", conditionRule.value);
	isDialogVisible.value = false;
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" persistent width="70vw" scrollable>
		<v-card elevation="9">
			<v-toolbar>
				<v-toolbar-title>Edit rule</v-toolbar-title>
				<v-spacer />
				<v-btn icon @click="isDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-divider />

			<v-card-text class="pa-5">
				<configure-conditions v-model="conditionRule.conditions" />
				<div class="mt-6">
					<configure-actions v-model="conditionRule.actions" />
				</div>
			</v-card-text>

			<v-divider />

			<v-card-actions class="pa-3">
				<div v-if="problems.length > 0" class="problems">
					<v-icon size="16" color="warning" class="mr-1">
						mdi-alert-circle-outline
					</v-icon>
					{{ problems.join(" ") }}
				</div>
				<v-spacer />
				<v-btn variant="text" color="error" @click="isDialogVisible = false">
					Cancel
				</v-btn>
				<v-btn
					class="ml-5"
					variant="tonal"
					color="primary"
					:disabled="!canSave"
					@click="storeConditionRule"
				>
					Save rule
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.problems {
	font-size: 0.85rem;
	opacity: 0.85;
	display: flex;
	align-items: center;
}
</style>
